'use client';

import React from 'react';
import PropTypes from 'prop-types';
import styled, {css} from 'styled-components';
import {p, pr, py, pl} from '@nib/layout';
import {
  copyColor,
  copyFontFamily,
  copyFontSizeSmall,
  formBorderColor,
  formBorderColorValid,
  formBorderColorInvalid,
  formInputBackgroundColor,
  focusOutlineColor,
  focusOutlineStyle,
  focusOutlineWidth,
  focusOutlineOffset
} from '@nib-components/theme';

const disabledCursor = 'not-allowed';
const disabledOpacity = 0.5;

const validFormModes = ['light', 'white'] as const;
type FormModes = (typeof validFormModes)[number];

const validInputModes = ['text', 'search', 'none', 'tel', 'url', 'email', 'numeric', 'decimal'] as const;
type InputModes = (typeof validInputModes)[number];

export interface TextboxProps {
  disabled?: boolean;
  valid?: boolean;
  validated?: boolean;
  formMode?: FormModes;
  inputMode?: InputModes;
  isCompact?: boolean;
  isEmptyAndOptional?: boolean;
  [key: string]: unknown;
}

export const StyledTextbox = styled.input<TextboxProps>`
  display: block;
  width: 100%;
  ${py(4)};
  ${pl(4)};

  /* Non-standard padding to accommodate validation icon */
  padding-right: 3.5rem;

  color: ${copyColor};
  font-size: 1rem;
  font-family: ${copyFontFamily};
  line-height: 1.5;

  border: 0;
  box-shadow: inset 0 -2px 0 0 ${formBorderColor};
  border-radius: 0;

  box-sizing: border-box;
  -webkit-appearance: none;

  &::-ms-clear {
    display: none;
  }

  /* To hide up/down arrows if type="number" */
  -moz-appearance: textfield;
  ::-webkit-outer-spin-button,
  ::-webkit-inner-spin-button {
    margin: 0;
    -webkit-appearance: none;
  }

  background-color: ${formInputBackgroundColor};

  ${({isCompact}) =>
    isCompact &&
    css`
      ${p(2)};
      ${pr(6)};
      font-size: ${copyFontSizeSmall};
    `};

  ${({disabled}) =>
    disabled &&
    css`
      opacity: ${disabledOpacity};
      cursor: ${disabledCursor};
    `}

  /* Validated and invalid */
  ${({valid, validated}) =>
    !valid &&
    validated &&
    css`
      box-shadow: inset 0 0 0 2px ${formBorderColorInvalid};
    `}

  /* Validated and valid */
  ${({valid, validated, isEmptyAndOptional}) =>
    valid &&
    validated &&
    !isEmptyAndOptional &&
    css`
      box-shadow: inset 0 -2px 0 0 ${formBorderColorValid};
    `}

  &:focus {
    outline: ${focusOutlineWidth} ${focusOutlineStyle} ${focusOutlineColor};
    outline-offset: ${focusOutlineOffset};
  }
`;

class Textbox extends React.Component<TextboxProps> {
  input: any = null;

  get selectionStart(): number {
    return this.input.selectionStart;
  }

  set selectionStart(start: number) {
    this.input.selectionStart = start;
  }

  get selectionEnd(): number {
    return this.input.selectionEnd;
  }

  set selectionEnd(end: number) {
    this.input.selectionEnd = end;
  }

  render(): React.ReactNode {
    const {disabled, valid, validated, formMode, inputMode, isEmptyAndOptional, ...otherProps} = this.props;

    return (
      <StyledTextbox
        {...otherProps}
        ref={(input: HTMLInputElement) => (this.input = input)} //eslint-disable-line no-return-assign
        disabled={disabled}
        valid={valid}
        validated={validated}
        formMode={formMode}
        inputMode={inputMode}
        isEmptyAndOptional={isEmptyAndOptional}
      />
    );
  }

  static displayName = 'Textbox';

  static propTypes = {
    disabled: PropTypes.bool,
    valid: PropTypes.bool,
    validated: PropTypes.bool,
    formMode: PropTypes.oneOf(['light', 'white']),
    inputMode: PropTypes.string,
    isCompact: PropTypes.bool,
    isEmptyAndOptional: PropTypes.bool
  };

  static defaultProps = {
    valid: false,
    validated: false,
    isEmptyAndOptional: false
  };
}
export default Textbox;
